import React, { FC, useMemo, useRef, useState } from 'react';
import {
  AttentionBoxVariant,
  AvAttentionBox,
  AvCheckbox,
  AvDialog,
  AvMessagePopover,
  AvTagsList,
  AvTooltip,
  CopyToClipboard,
  CopyType,
  FeatureFlags,
  Initials,
  Select,
  TextInput,
} from '@fabric-ui/components';
import { ApplicationLayerProtocol, Delete, Export, QuestionFull, Web, X } from '@fabric-ui/icons';
import { AlertsFull, AssetsFull, FindingsFull, IdentitiesFull, IncidentsFull, Risk360Full, ThreatsFull } from '@fabric-ui/icons/apps';
import { WarningFullOrange } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { difference, iconSize, intersection, noop, uniqBy } from '@fabric-ui/utils';
import { Box, Button, Divider, IconButton, Popover, SxProps, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAvContext } from '../../../context/AvContextProvider';
import { useAllApps } from '../../../hooks/useAllApps';
import { useGetUsersInfo } from '../../../hooks/useGetUsersInfo';
import { APP_PATHS, getRoleNameByValue, PAGE_PATHS, Permission, ROLES } from '../../../types';
import { isNullOrUndefined } from '../../../utils/utils';
import { useGetAllDashboardAccessRoles } from '../../AccountManagement/hooks';
import { PermissionSetType } from '../../ContentPermissions/ContentPermission.types';
import { useContentPermissions, useTags } from '../../ContentPermissions/hooks';
import { ModeType } from '../constants';
import { useSharableReportUsers } from '../hooks';
import { useDashboardsTagsList } from '../hooks/useDashboardsTagsList';
import { CustomDashboardDto, DashboardReportPermission, publicId, UserPermissionType } from '../types';
import { DashboardsTagsSelect } from './DashboardsTagsSelect';
import {
  buildEditorsSelectValue,
  buildViewersSelectValue,
  getAttentionMessage,
  getNewTagPermissionSetEntries,
  getPermissionType,
  getTagDerivedPermissionSetIds,
  PermissionSetEntry,
  setPermissionSetsLevel,
  splitByIds,
  TAG_MANAGED_PERMISSION_SET_TOOLTIP,
  withDisabledIds,
} from './EditDashboardDetailsWindow.utils';

const exitIcon = <X style={iconSize(26)} />;
const deleteIcon = <Delete style={iconSize(18)} />;
const exportIcon = (size = 14, iconStyle = {}) => <Export style={{ ...iconSize(size), ...iconStyle }} />;
const orangeWarningIcon = <WarningFullOrange />;

const iconComponents = {
  VULNERABILITIES: FindingsFull,
  DETECTIONS: AlertsFull,
  INCIDENTS: IncidentsFull,
  RISK360: Risk360Full,
  ASSETS: AssetsFull,
  THREATS: ThreatsFull,
  IDENTITIES: IdentitiesFull,
};

interface EditDashboardDetailsWindowProps {
  title?: string;
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  anchorEl?: Element;
  setAnchorEl?: React.Dispatch<React.SetStateAction<Element | undefined>>;
  initData: Partial<CustomDashboardDto>;
  onSuccess: (dashboard: Partial<CustomDashboardDto>) => void;
  allowImportContent?: boolean;
  disabled?: boolean;
  onSuccessSaveDashboard?: boolean;
  modeType?: ModeType;
  buttonText?: string;
  warning?: string;
  disableBackdropClick?: boolean;
}

const PublicOption = { value: publicId, title: 'Public', iconFull: <Web style={iconSize(24)} />, group: 'Public' };
const EditDashboardDetailsWindow: FC<EditDashboardDetailsWindowProps> = props => {
  const {
    title = 'Dashboard',
    anchorEl,
    setAnchorEl = noop,
    isOpen = false,
    setIsOpen = noop,
    initData,
    onSuccess,
    allowImportContent = false,
    disabled = false,
    onSuccessSaveDashboard = false,
    modeType = ModeType.Create,
    buttonText,
    warning,
    disableBackdropClick = false,
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [newTagPermissionSets, setNewTagPermissionSets] = useState<{ editors: PermissionSetEntry[]; viewers: PermissionSetEntry[] }>({
    editors: [],
    viewers: [],
  });

  const initialUsersOfDashboard = useMemo(
    () =>
      [...(initData.dashboardReportPermission?.editors || []), ...(initData.dashboardReportPermission?.viewers || [])].filter(
        user => user !== publicId
      ),
    []
  );
  const doClose = () => {
    // closing the popover is what commits the changes, so a read-only form must not propagate anything
    if (anchorEl && !disabled) {
      onSuccess(newDashboardData);
    }
    setAnchorEl(undefined);
    setIsOpen(false);
  };

  const onClose = (event?, reason?) => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    if (isPermissionSetSharingEnabled) {
      const newEntries = getNewTagPermissionSetEntries(initData, newDashboardData, tags);
      if (newEntries.editors.length || newEntries.viewers.length) {
        setNewTagPermissionSets(newEntries);
        setConfirmationModalOpen(true);
        return;
      }
    }
    doClose();
  };

  const [newDashboardData, setNewDashboardData] = useState<Partial<CustomDashboardDto>>(initData);
  const {
    userPermissions: { hasAllowedPermission },
    enabledApps,
    featureFlags,
  } = useAvContext();
  const isPermissionSetSharingEnabled = featureFlags[FeatureFlags.DashboardReportPermissionSetSharing];
  const [attachedFileName, setAttachedFileName] = useState<string | undefined>(undefined);
  const fileInputRef = useRef<any>(undefined);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();

      reader.onload = event => {
        const result = event.target?.result;
        try {
          if (result && typeof result === 'string') {
            const jsonData = JSON.parse(result);
            setAttachedFileName(file.name);
            if (Array.isArray(jsonData)) {
              onChange('widgets')(jsonData);
            } else if (
              jsonData &&
              typeof jsonData === 'object' &&
              Array.isArray(jsonData.widgets ?? []) &&
              typeof (jsonData.globalFilters ?? {}) === 'object' &&
              !Array.isArray(jsonData.globalFilters ?? {})
            ) {
              setNewDashboardData(prev => ({ ...prev, widgets: jsonData.widgets ?? [], globalFilters: jsonData.globalFilters ?? {} }));
            } else {
              enqueueSnackbar('Invalid dashboard JSON format', { variant: 'error' });
            }
          } else {
            enqueueSnackbar('Unable to parse JSON', { variant: 'error' });
          }
        } catch {
          enqueueSnackbar('Unable to parse JSON', { variant: 'error' });
        } finally {
          fileInputRef.current.value = '';
        }
      };

      reader.readAsText(file);
    }
  };

  const handleAttachmentClick = () => fileInputRef.current.click();
  const { data: AllApps = [] } = useAllApps();
  const isAllowedShareableUsers = hasAllowedPermission({ path: PAGE_PATHS.REPORTS, permission: Permission.UPDATE });
  const { data: accountDashboardAccessRoles = [] } = useGetAllDashboardAccessRoles();
  const { data: users = [], isLoading: isLoadingAllUsers } = useSharableReportUsers(isAllowedShareableUsers);

  const { data: dashboardTags = [] } = useDashboardsTagsList();
  const { data: tags = [] } = useTags({ enabled: !!isPermissionSetSharingEnabled });
  const { usersInfo: currentDashboardUsers, isLoading: isLoadingUsers } = useGetUsersInfo(
    uniqBy(isAllowedShareableUsers ? [] : initialUsersOfDashboard, v => v),
    data => data,
    []
  );
  const { data: permissionSets, isLoading: isLoadingPermissionSets } = useContentPermissions({ enabled: !!isPermissionSetSharingEnabled });
  const oltpPermissionSets = useMemo(
    () => (isPermissionSetSharingEnabled ? (permissionSets ?? []).filter(ps => ps.type === PermissionSetType.OLTPEntity && ps.id) : []),
    [permissionSets, isPermissionSetSharingEnabled]
  );
  const isLoading = isLoadingAllUsers || isLoadingUsers || (isPermissionSetSharingEnabled && isLoadingPermissionSets);

  const usersOptionsForEditors = useMemo(
    () =>
      (isAllowedShareableUsers ? users : currentDashboardUsers).map(({ id, firstName, lastName }) => ({
        value: id,
        title: `${firstName} ${lastName}`,
        group: 'Users',
      })),
    [users, currentDashboardUsers, isAllowedShareableUsers]
  );
  const allowedToViewUsersSet = useMemo(
    () => new Set(newDashboardData.dashboardReportPermission?.viewers || []),
    [newDashboardData.dashboardReportPermission?.viewers]
  );

  const allowedToEditUsersSet = useMemo(
    () => new Set(newDashboardData.dashboardReportPermission?.editors || []),
    [newDashboardData.dashboardReportPermission?.editors]
  );

  const usersOptionsForViewers = useMemo(
    () => usersOptionsForEditors.map(option => ({ ...option, disabled: allowedToEditUsersSet.has(option.value) })),
    [usersOptionsForEditors, newDashboardData.dashboardReportPermission?.editors]
  );

  const permissionSetOptionIds = useMemo(() => oltpPermissionSets.map(ps => ps.id as string), [oltpPermissionSets]);
  const permissionSetOptionsBase = useMemo(
    () =>
      oltpPermissionSets.map(ps => ({
        value: ps.id as string,
        title: ps.name,
        group: 'Permission Sets',
        iconFull: <ApplicationLayerProtocol viewBox="0 0 24 24" style={iconSize(24)} />,
      })),
    [oltpPermissionSets]
  );
  const { editorPermissionSetIds, viewerPermissionSetIds } = useMemo(
    () =>
      (newDashboardData.dashboardReportPermission?.permissionSets ?? []).reduce(
        (acc, link) => {
          acc[link.sharedType === UserPermissionType.editor ? 'editorPermissionSetIds' : 'viewerPermissionSetIds'].add(
            link.permissionSetId
          );
          return acc;
        },
        { editorPermissionSetIds: new Set<string>(), viewerPermissionSetIds: new Set<string>() }
      ),
    [newDashboardData.dashboardReportPermission?.permissionSets]
  );

  const { tagEditorIds: tagEditorPermissionSetIds, tagViewerIds: tagViewerPermissionSetIds } = useMemo(
    () =>
      getTagDerivedPermissionSetIds(newDashboardData.dashboardReportPermission?.permissionSets ?? [], tags, newDashboardData.tags ?? []),
    [tags, newDashboardData.tags, newDashboardData.dashboardReportPermission?.permissionSets]
  );

  const permissionSetOptionsForViewers = useMemo(
    () =>
      withDisabledIds(
        permissionSetOptionsBase,
        [...editorPermissionSetIds],
        [...tagEditorPermissionSetIds, ...tagViewerPermissionSetIds],
        TAG_MANAGED_PERMISSION_SET_TOOLTIP
      ),
    [permissionSetOptionsBase, editorPermissionSetIds, tagEditorPermissionSetIds, tagViewerPermissionSetIds]
  );
  const permissionSetOptionsForEditors = useMemo(
    () => withDisabledIds(permissionSetOptionsBase, [], tagEditorPermissionSetIds, TAG_MANAGED_PERMISSION_SET_TOOLTIP),
    [permissionSetOptionsBase, tagEditorPermissionSetIds]
  );

  const attentionMessage = getAttentionMessage(
    isPermissionSetSharingEnabled,
    !!newDashboardData.tags?.length,
    !!(editorPermissionSetIds.size || tagEditorPermissionSetIds.length)
  );

  const filteredAppsOption = enabledApps.map(({ id, name, backgroundColor, enumValue }) => {
    const IconComponent = iconComponents[enumValue];
    return {
      value: AllApps.find(({ name }) => id === APP_PATHS[name])?.name,
      title: name,
      iconFull: IconComponent ? <IconComponent /> : undefined,
      backgroundColor,
    };
  });

  const tagsOptions = useMemo(() => dashboardTags.map(tag => ({ value: tag, title: tag })), [dashboardTags]);

  const onSave = () => {
    if (onSuccessSaveDashboard) {
      return;
    }
    onSuccess(newDashboardData);
    onClose();
  };

  const onChange = field => value => {
    const newData = { ...newDashboardData, [field]: value };
    setNewDashboardData(newData);
  };
  const getViewersList = (viewers, newEditorsList) => {
    const newEditorsSet = new Set(newEditorsList);
    const newEditorRemoved = difference(allowedToEditUsersSet, newEditorsSet).values().next().value;
    if (newEditorRemoved) {
      return [...viewers, newEditorRemoved];
    }
    const newEditorAdded = intersection(newEditorsSet, allowedToViewUsersSet).values().next().value;
    if (newEditorAdded && allowedToViewUsersSet.has(newEditorAdded as string)) {
      return viewers.filter(v => v !== newEditorAdded);
    }
    return viewers;
  };
  const renderFileInput = <input accept=".json" ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} />;
  const onChangeDashboardReportPermission = (field: 'viewers' | 'editors') => (value: string[]) => {
    if (newDashboardData.dashboardReportPermission) {
      const { viewers } = newDashboardData.dashboardReportPermission;
      const newDashboardReportPermission: DashboardReportPermission = {
        ...newDashboardData.dashboardReportPermission,
        [field]: value,
        ...(field === 'editors' && {
          viewers: value.includes(publicId) || viewers.includes(publicId) ? [publicId] : getViewersList(viewers, value),
        }),
      };

      const newValue = {
        ...newDashboardData,
        dashboardReportPermission: {
          ...newDashboardReportPermission,
          type: getPermissionType(newDashboardReportPermission),
        },
      };

      setNewDashboardData(newValue);
    }
  };

  const onChangePermissionSets = (sharedType: UserPermissionType) => (selectedIds: string[]) => {
    if (newDashboardData.dashboardReportPermission) {
      setNewDashboardData(prev => {
        const newDashboardReportPermission: DashboardReportPermission = {
          ...prev!.dashboardReportPermission!,
          permissionSets: setPermissionSetsLevel(
            prev!.dashboardReportPermission!.permissionSets ?? [],
            permissionSetOptionIds,
            selectedIds,
            sharedType
          ),
        };
        return {
          ...prev,
          dashboardReportPermission: { ...newDashboardReportPermission, type: getPermissionType(newDashboardReportPermission) },
        };
      });
    }
  };

  const viewersValue = useMemo(
    () =>
      allowedToViewUsersSet.has(publicId)
        ? newDashboardData!.dashboardReportPermission!.viewers
        : [
            ...(newDashboardData!.dashboardReportPermission?.viewers ?? []),
            ...(newDashboardData!.dashboardReportPermission?.editors.filter(v => !allowedToViewUsersSet.has(v)) ?? []),
          ],
    [allowedToViewUsersSet, newDashboardData!.dashboardReportPermission?.editors]
  );

  const isViewersPublic = newDashboardData.dashboardReportPermission?.viewers.includes(publicId);
  const isEditorsPublic = newDashboardData.dashboardReportPermission?.editors.includes(publicId);

  const viewerOptions = [PublicOption, ...(isPermissionSetSharingEnabled ? permissionSetOptionsForViewers : []), ...usersOptionsForViewers];

  const editorsOptions = [
    PublicOption,
    ...(isPermissionSetSharingEnabled ? permissionSetOptionsForEditors : []),
    ...usersOptionsForEditors,
  ];

  const viewersSelectValue = useMemo(
    () =>
      buildViewersSelectValue(
        !!isViewersPublic,
        viewersValue,
        isPermissionSetSharingEnabled
          ? {
              viewerIds: viewerPermissionSetIds,
              editorIds: editorPermissionSetIds,
              tagEditorIds: tagEditorPermissionSetIds,
              tagViewerIds: tagViewerPermissionSetIds,
            }
          : null
      ),
    [
      isViewersPublic,
      viewersValue,
      isPermissionSetSharingEnabled,
      viewerPermissionSetIds,
      editorPermissionSetIds,
      tagEditorPermissionSetIds,
      tagViewerPermissionSetIds,
    ]
  );
  const editorsSelectValue = useMemo(
    () =>
      buildEditorsSelectValue(
        !!isEditorsPublic,
        newDashboardData.dashboardReportPermission?.editors ?? [],
        isPermissionSetSharingEnabled ? { editorIds: editorPermissionSetIds, tagEditorIds: tagEditorPermissionSetIds } : null
      ),
    [
      isEditorsPublic,
      newDashboardData.dashboardReportPermission?.editors,
      isPermissionSetSharingEnabled,
      editorPermissionSetIds,
      tagEditorPermissionSetIds,
    ]
  );

  const commonProps = fieldName =>
    ({
      size: 'medium',
      disabled,
      value: newDashboardData[fieldName],
      onChange: onChange(fieldName),
    }) as any;

  const selectCommonStyle = {
    '&.autocomplete-multiple': {
      '&, & .MuiFormControl-root': { maxWidth: '100%' },
      '.MuiOutlinedInput-root.MuiAutocomplete-inputRoot': { py: '5px' },
    },
  };

  const renderOption = ({
    title,
    iconFull = <Initials name={title} size="xSmall" />,
    sx,
    tooltip,
  }: {
    title: string;
    iconFull: React.JSX.Element;
    sx?: SxProps;
    tooltip?: string;
  }) => (
    <AvTooltip title={tooltip ?? ''}>
      <Box sx={{ ...flex.itemsCenter, gap: 1, pointerEvents: 'auto' }}>
        <Box sx={{ ...sx, borderRadius: '8px', ...flex.center }}>{iconFull}</Box>
        {title}
      </Box>
    </AvTooltip>
  );

  const TitleGenerator = {
    Edit: `Edit ${title}`,
    SaveAs: `Save as a New ${title}`,
    Share: `Share ${title}`,
    Create: `Create New ${title}`,
  };

  const ButtonGenerator = {
    Edit: 'Save',
    SaveAs: 'Create',
    Share: 'Done',
    Create: 'Create',
  };

  const isSaveDisabled = !newDashboardData.name && modeType !== ModeType.Share;

  const removeFileInput = () => {
    setAttachedFileName(undefined);
    onChange('widgets')([]);
  };

  const inputs = (
    <Box sx={{ gap: 2, ...flex.col, mb: 3 }}>
      {allowImportContent && renderFileInput}
      {attachedFileName && (
        <>
          <Box sx={{ ...flex.justifyBetweenCenter }}>
            <Box sx={{ ...flex.row, gap: 1 }}>
              <Box sx={{ color: 'var(--color-content-base-secondary)' }}>Attached template file:</Box>
              <Box sx={{ color: 'var(--color-content-base-primary)' }}>{attachedFileName}</Box>
            </Box>
            <Box sx={{ gap: 2, ...flex.row }}>
              <AvTooltip title="Replace Template File">
                <IconButton onClick={handleAttachmentClick} disabled={disabled}>
                  {exportIcon(18)}
                </IconButton>
              </AvTooltip>
              <AvTooltip title="Delete File">
                <IconButton onClick={removeFileInput} disabled={disabled}>
                  {deleteIcon}
                </IconButton>
              </AvTooltip>
            </Box>
          </Box>
          <Divider />
        </>
      )}
      {!isNullOrUndefined(newDashboardData.name) && (
        <TextInput
          {...commonProps('name')}
          label="Name"
          sx={{ maxWidth: '100%' }}
          isRequired
          autoFocus={modeType === ModeType.Edit}
          disableClearButton
        />
      )}
      {!isNullOrUndefined(newDashboardData.description) && (
        <TextInput {...commonProps('description')} label="Description" sx={{ maxWidth: '100%' }} />
      )}
      {newDashboardData.dashboardReportPermission?.viewers && (
        <Select
          muiProps={{
            disableClearable: true,
            // @ts-expect-error Select is not typed correctly
            getOptionDisabled: (option: { value: string; disabled?: boolean }) =>
              (option.value === publicId ? isEditorsPublic : isViewersPublic) || !!option.disabled,
          }}
          label={
            <>
              Viewers
              {accountDashboardAccessRoles.some(role => role.name === getRoleNameByValue(ROLES.ADMIN)) && (
                <Box sx={{ position: 'absolute', display: 'inline', ml: 0.5 }}>
                  <AvMessagePopover message="Admins can view all dashboards" icon={<QuestionFull />} asTooltip />
                </Box>
              )}
            </>
          }
          loading={isLoading}
          // @ts-expect-error Select is not typed correctly
          value={viewersSelectValue}
          disabled={disabled || isEditorsPublic}
          onChange={v => {
            if (v.includes(publicId)) {
              onChangeDashboardReportPermission('viewers')([publicId]);
              if (isPermissionSetSharingEnabled) onChangePermissionSets(UserPermissionType.viewer)([]);
              return;
            }
            if (isPermissionSetSharingEnabled) {
              const { matching: selectedPermissionSetIds, rest: selectedUserIds } = splitByIds(v, permissionSetOptionIds);
              onChangeDashboardReportPermission('viewers')(selectedUserIds.filter(id => !allowedToEditUsersSet.has(id)));
              onChangePermissionSets(UserPermissionType.viewer)(selectedPermissionSetIds.filter(id => !editorPermissionSetIds.has(id)));
            } else {
              onChangeDashboardReportPermission('viewers')(v.filter(id => !allowedToEditUsersSet.has(id)));
            }
          }}
          isMultiple
          groupByFunc={({ group }: any) => group}
          options={viewerOptions}
          showClearSelection
          filterFunc={(options: any, { inputValue }) => options.filter(o => o.title.toLowerCase().includes(inputValue.toLowerCase()))}
          renderOption={({ title, iconFull, tooltip }: any) => renderOption({ title, iconFull, tooltip, sx: { p: '0' } })}
          renderOptionStyle={({ value }: any) =>
            value === publicId && !newDashboardData.dashboardReportPermission?.viewers.includes(publicId)
              ? { borderBottom: `1px solid var(--color-border-base-primary)` }
              : {}
          }
          style={selectCommonStyle}
        />
      )}
      {[ModeType.Create, ModeType.Edit].includes(modeType) && (
        <AvCheckbox
          label="Allow viewers to change dashboard level filters"
          value={newDashboardData.allowViewersToChangeFilters}
          onChange={onChange('allowViewersToChangeFilters')}
          disabled={disabled}
        />
      )}
      {newDashboardData.dashboardReportPermission?.editors && (
        <Select
          muiProps={{
            disableClearable: true,
            // @ts-expect-error Select is not typed correctly
            getOptionDisabled: (option: { value: string; disabled?: boolean }) =>
              (option.value !== publicId && isEditorsPublic) || !!option.disabled,
          }}
          label="Editors"
          loading={isLoading}
          disabled={disabled}
          // @ts-expect-error Select is not typed correctly
          value={editorsSelectValue}
          onChange={v => {
            if (v.includes(publicId)) {
              onChangeDashboardReportPermission('editors')([publicId]);
              if (isPermissionSetSharingEnabled) onChangePermissionSets(UserPermissionType.editor)([]);
              return;
            }
            if (isPermissionSetSharingEnabled) {
              const { matching: selectedPermissionSetIds, rest: selectedUserIds } = splitByIds(v, permissionSetOptionIds);
              onChangeDashboardReportPermission('editors')(selectedUserIds);
              onChangePermissionSets(UserPermissionType.editor)(selectedPermissionSetIds);
            } else {
              onChangeDashboardReportPermission('editors')(v);
            }
          }}
          isMultiple
          groupByFunc={({ group }: any) => group}
          options={editorsOptions}
          showClearSelection
          filterFunc={(options: any, { inputValue }) => options.filter(o => o.title.toLowerCase().includes(inputValue.toLowerCase()))}
          renderOption={({ title, iconFull, tooltip }: any) => renderOption({ title, iconFull, tooltip, sx: { p: '0' } })}
          renderOptionStyle={({ value }: any) => (value === publicId ? { borderBottom: `1px solid var(--color-border-base-primary)` } : {})}
          style={selectCommonStyle}
        />
      )}
      {newDashboardData.apps && (
        <Select
          muiProps={{ disableClearable: true }}
          label="Pin to Apps"
          {...commonProps('apps')}
          isMultiple
          options={filteredAppsOption}
          renderOption={({ title, iconFull }: any) => renderOption({ title, iconFull, sx: { svg: { width: 22, height: 22 } } })}
          style={selectCommonStyle}
        />
      )}
      {newDashboardData.tags && modeType !== ModeType.Share && (
        <DashboardsTagsSelect
          value={newDashboardData.tags}
          onChange={onChange('tags')}
          options={tagsOptions}
          style={selectCommonStyle}
          disabled={disabled}
        />
      )}
      {attentionMessage && <AvAttentionBox variant={AttentionBoxVariant.Neutral} content={attentionMessage} />}
      {warning && (
        <Box sx={{ ...flex.row, gap: 1, mt: 1 }}>
          {orangeWarningIcon} <span>{warning}</span>
        </Box>
      )}
    </Box>
  );

  const confirmationModal = isPermissionSetSharingEnabled && (
    <AvDialog
      open={isConfirmationModalOpen}
      onClose={() => setConfirmationModalOpen(false)}
      title="Adding tags will also update dashboard access"
      footer={
        <Box sx={{ ...flex.justifyBetween, gap: 2 }}>
          <Button onClick={() => setConfirmationModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmationModalOpen(false);
              doClose();
            }}>
            Continue
          </Button>
        </Box>
      }>
      <Box sx={{ ...flex.col, gap: 2 }}>
        <Typography variant="body2">
          One or more of the tags you added are linked to permission sets. Applying these tags will grant users in those permission sets
          access to this dashboard, according to the access level defined in each permission set.
        </Typography>
        <Divider flexItem />
        <Typography variant="h6">Sharing Updates</Typography>
        <Box
          component="span"
          display="flex"
          sx={{
            color: 'var(--color-content-base-tertiary)',
            fontFamily: 'Inter',
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '21px',
            alignItems: 'center',
            gap: 2.5,
          }}>
          New Viewers:
          {newTagPermissionSets.viewers.length ? (
            <Box
              sx={{
                '& div': { flexWrap: 'wrap', overflow: 'visible', whiteSpace: 'normal', WebkitLineClamp: 'unset', textOverflow: 'clip' },
              }}>
              <AvTagsList tags={newTagPermissionSets.viewers.map(ps => ps.name)} numOfTags={newTagPermissionSets.viewers.length} />
            </Box>
          ) : (
            <Box component="span" sx={{ fontWeight: 'normal', color: 'var(--color-content-base-primary)' }}>
              No Change
            </Box>
          )}
        </Box>

        <Box
          component="span"
          display="flex"
          sx={{
            color: 'var(--color-content-base-tertiary)',
            fontFamily: 'Inter',
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '21px',
            alignItems: 'center',
            gap: 2.5,
          }}>
          New Editors:
          {newTagPermissionSets.editors.length ? (
            <Box
              sx={{
                '& div': { flexWrap: 'wrap', overflow: 'visible', whiteSpace: 'normal', WebkitLineClamp: 'unset', textOverflow: 'clip' },
              }}>
              <AvTagsList tags={newTagPermissionSets.editors.map(ps => ps.name)} numOfTags={newTagPermissionSets.editors.length} />
            </Box>
          ) : (
            <Box component="span" sx={{ fontWeight: 'normal', color: 'var(--color-content-base-primary)' }}>
              No Change
            </Box>
          )}
        </Box>
      </Box>
    </AvDialog>
  );

  return (
    <>
      {confirmationModal}
      {anchorEl ? (
        <Popover
          sx={{ mt: 1.5 }}
          onClick={e => e.stopPropagation()}
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          onClose={onClose}>
          <Box sx={{ ...flex.col, width: 550 }}>
            <Box sx={{ px: 3, gap: 1, ...flex.col, pt: 2, pb: 6 }}>
              <Box sx={{ ...flex.justifyBetweenCenter, py: 1.5, pl: -1 }}>
                <Typography variant="h5">{`Edit ${title} Details`}</Typography>
                <Button aria-label={`Close ${title} Details`} onClick={onClose}>
                  {exitIcon}
                </Button>
              </Box>
              {inputs}
            </Box>
          </Box>
        </Popover>
      ) : (
        <AvDialog
          fullWidth
          size="sm"
          open={isOpen}
          onClose={onClose}
          PaperProps={{ sx: { '& .MuiDialogContent-root': { maxWidth: 'unset' } } }}
          title={
            <Box sx={{ ...flex.justifyBetweenCenter, width: '100%', gap: 1 }}>
              {TitleGenerator[ModeType[modeType]]}
              {allowImportContent && (
                <Button size="xSmall" variant="outlined" onClick={handleAttachmentClick} disabled={disabled}>
                  {exportIcon(undefined, { transform: 'rotate(180deg)' })} Import Template
                </Button>
              )}
            </Box>
          }
          footer={
            <Box sx={flex.row}>
              {modeType === ModeType.Share && (
                <CopyToClipboard
                  title="Copy Dashboard Link"
                  copyData={window.location.origin + window.location.pathname}
                  copyType={CopyType.LINK}
                />
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ ...flex.justifyEndCenter, gap: 2 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={onSave} disabled={isSaveDisabled || disabled}>
                  {buttonText || ButtonGenerator[ModeType[modeType]]}
                </Button>
              </Box>
            </Box>
          }>
          {inputs}
        </AvDialog>
      )}
    </>
  );
};

export default EditDashboardDetailsWindow;
